import { t } from "@hakili/i18n";
import { useEffect, useState } from "react";

import type { Vue } from "../shell/views.js";

/**
 * `round CN-C`, `CN-C.1`, WF-CON-38: **« Priorités », to the drawing
 * (`hakili-console.jsx`, `PrioritesScreen`, `ListeSignaux`).**
 *
 * The screen COMPOSES nothing: the signals come from `GET /partners/signals`,
 * each derived by the door over the portfolio's own rows and sorted rose,
 * amber, grey there; a signal a module does not answer yet comes back in
 * words and is printed as the drawing's row, greyed, with the module that
 * owes it (`R81`). A gesture is done BY the journal door with its origin
 * (« Programmer un point » logs the exchange), and the confirmation the row
 * prints afterwards is the door's, read from the journal, never a flag here.
 *
 * The indicators the drawing computes over fields no module answers today
 * (dossiers submitted this month, vehicles listed this month, prospects) are
 * dashes with their owner named; the active-partner count is the signals
 * door's portfolio, which the fiche's directory already carries.
 */

export type Severity = "rose" | "amber" | "gris";
export type PartnerType = "banque" | "concessionnaire" | "assureur";

export interface SignalAction {
  readonly label: string;
  readonly primary: boolean;
  readonly kind: "journal" | "open";
  readonly origin?: string;
  readonly channel?: string;
  readonly body?: string;
}

export interface SignalView {
  readonly key: string;
  readonly severity: Severity;
  readonly type: PartnerType;
  readonly organisationId: string;
  readonly partnerName: string;
  readonly title: string;
  readonly reason: string;
  readonly confirmation: string | null;
  readonly actions: readonly SignalAction[];
}

export interface SignalInWordsView {
  readonly number: number;
  readonly title: string;
  readonly sentence: string;
  readonly owner: string;
  readonly type: PartnerType;
}

export interface SignalsView {
  readonly types: readonly PartnerType[];
  readonly partnerCount: number;
  readonly signals: readonly SignalView[];
  readonly inWords: readonly SignalInWordsView[];
}

/**
 * `round K-1`, `M3 (d)`: WF-CON-38's signals 11 « Réservation prolongée » and 12
 * « Réservation ancienne » (amended by record), derived by `dossier` on every
 * read (`GET /dossiers/signals`) , they clear themselves when the buyer moves.
 */
export interface HoldSignalView {
  readonly number: 11 | 12;
  readonly key: string;
  readonly title: string;
  readonly dossierId: string;
  readonly reference: string;
  readonly clientName: string;
  readonly reason: string;
  readonly days: number;
}

export interface PrioritesDoors {
  readonly readSignals: () => Promise<SignalsView>;
  /** `K-1`, `M3 (d)`: the two reservation signals, `dossier`'s own door. */
  readonly readHoldSignals?: () => Promise<readonly HoldSignalView[]>;
  /** `K-1`: « Relancer le client », the file's own chase door (`POST /dossiers/{id}/pieces/relance-all`). */
  readonly chaseClient?: (dossierId: string) => Promise<void>;
  /** The journal door with the gesture's origin , one truth per gesture. */
  readonly logExchange: (
    organisationId: string,
    input: { readonly type: string; readonly body: string; readonly origin: string },
  ) => Promise<void>;
}

export interface PrioritesProps {
  readonly vue: Vue;
  readonly signals: SignalsView | null;
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly onAction: (signal: SignalView, action: SignalAction) => void;
  readonly onOpenPartner: (organisationId: string) => void;
  /** `K-1`, `M3 (d)`: the reservation signals and their two gestures. */
  readonly holdSignals?: readonly HoldSignalView[] | null;
  readonly onOpenDossier?: (dossierId: string) => void;
  readonly onChaseClient?: (dossierId: string) => void;
}

const DASH = t("common.valeur.tiret");

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

/** The drawing's `severites`: rose, amber, grey , the closed palette's shades. */
const SEVERITY_DOT = new Map<Severity, string>([
  ["rose", "bg-rose-500"],
  ["amber", "bg-amber-500"],
  ["gris", "bg-gray-300"],
]);

type Filter = "tous" | PartnerType;

const FILTERS: readonly { readonly id: Filter; readonly key: Parameters<typeof t>[0] }[] = [
  { id: "tous", key: "console.priorites.filtreTous" },
  { id: "banque", key: "console.priorites.filtreBanques" },
  { id: "concessionnaire", key: "console.priorites.filtreConcess" },
  { id: "assureur", key: "console.priorites.filtreAssureurs" },
];

const GROUP_KEY = new Map<PartnerType, Parameters<typeof t>[0]>([
  ["banque", "console.priorites.groupeBanques"],
  ["concessionnaire", "console.priorites.groupeConcess"],
  ["assureur", "console.priorites.groupeAssureurs"],
]);

const SUBTITLE = new Map<Vue, Parameters<typeof t>[0]>([
  ["banques", "console.priorites.sousTitreBanques"],
  ["concess", "console.priorites.sousTitreConcess"],
]);

export function Priorites(props: PrioritesProps): React.JSX.Element {
  const [filter, setFilter] = useState<Filter>("tous");
  const admin = props.vue === "admin";
  const types: readonly PartnerType[] =
    props.signals === null ? [] : admin && filter !== "tous" ? [filter] : props.signals.types;
  const signals = (props.signals?.signals ?? []).filter((one) => types.includes(one.type));
  const inWords = (props.signals?.inWords ?? []).filter((one) => types.includes(one.type));
  // The agenda: the gestures the journal already holds, read off the signals' confirmations.
  const actions = signals.filter((one) => one.confirmation !== null);
  const groups: { readonly type: PartnerType | null; readonly items: readonly SignalView[] }[] =
    types.length === 1
      ? [{ type: null, items: signals }]
      : (["banque", "concessionnaire", "assureur"] as const).map((type) => ({
          type,
          items: signals.filter((one) => one.type === type),
        }));
  const kpis: readonly {
    readonly label: string;
    readonly value: string;
    readonly sub?: string;
    readonly highlight?: boolean;
  }[] = [
    {
      label: t("console.priorites.kpiPartenairesActifs"),
      value: props.signals === null ? DASH : String(props.signals.partnerCount),
      sub: fill("console.priorites.kpiPartenairesSous", {
        n: props.signals === null ? DASH : String(props.signals.partnerCount),
      }),
    },
    { label: t("console.priorites.kpiSoumis"), value: DASH },
    {
      label: t("console.priorites.kpiMisEnLigne"),
      value: DASH,
      sub: t("console.priorites.kpiMisEnLigneSous"),
      highlight: true,
    },
    { label: t("console.priorites.kpiProspects"), value: DASH, sub: t("console.priorites.kpiProspectsSous") },
  ];

  return (
    <div data-testid="priorites">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t("console.priorites.titre")}</h1>
        <p className="mt-1.5 text-sm text-gray-500">
          {t(SUBTITLE.get(props.vue) ?? "console.priorites.sousTitreAdmin")}
        </p>
      </div>

      {admin ? (
        <div data-testid="priorites-filtre" className="mb-4 flex items-center gap-2">
          {FILTERS.map((one) => (
            <button
              key={one.id}
              type="button"
              data-testid={`priorites-filtre-${one.id}`}
              onClick={() => {
                setFilter(one.id);
              }}
              className={
                "rounded-full border px-3 py-1.5 text-xs font-medium " +
                (filter === one.id
                  ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50")
              }
            >
              {t(one.key)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
            <p className="text-xs text-gray-500">{kpi.label}</p>
            <p
              className={
                "mt-1 text-2xl font-semibold tabular-nums " +
                (kpi.highlight === true ? "text-indigo-600" : "")
              }
            >
              {kpi.value}
            </p>
            {kpi.sub === undefined ? null : <p className="mt-1 text-xs text-gray-500">{kpi.sub}</p>}
          </div>
        ))}
      </div>

      <h2 className="mb-1 text-sm font-semibold">{t("console.priorites.signauxTitre")}</h2>
      <p className="mb-3 text-sm text-gray-500">{t("console.priorites.signauxSous")}</p>
      {props.refusal === null ? null : (
        <p role="alert" data-testid="priorites-refus" className="mb-3 text-sm text-rose-600">
          {props.refusal}
        </p>
      )}
      <div
        data-testid="priorites-signaux"
        className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white"
      >
        {props.signals === null ? (
          <p className="px-5 py-5 text-sm text-gray-500">{t("common.state.chargement")}</p>
        ) : signals.length === 0 ? (
          <p data-testid="priorites-vide" className="px-5 py-5 text-sm text-gray-500">
            {t("console.priorites.signauxVide")}
          </p>
        ) : null}
        {groups.map((group) =>
          group.items.length === 0 ? null : (
            <div key={group.type ?? "unique"}>
              {group.type === null ? null : (
                <p className="border-b border-gray-100 bg-gray-50 px-5 py-2 text-xs font-medium text-gray-500">
                  {t(GROUP_KEY.get(group.type) ?? "console.priorites.groupeBanques")}
                </p>
              )}
              <ul className="divide-y divide-gray-50">
                {group.items.map((signal) => (
                  <li
                    key={signal.key}
                    data-testid={`signal-${signal.key}`}
                    className="flex items-start gap-3 px-5 py-4"
                  >
                    <span
                      className={
                        "mt-1.5 h-2 w-2 shrink-0 rounded-full " +
                        (SEVERITY_DOT.get(signal.severity) ?? "bg-gray-300")
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{signal.title}</p>
                        {/*
                         * `FX-3`, `M1`: **the badge that could never fill is gone.** It was
                         * `<ManagerBadge name={null} />`, a literal, so every signal on this screen drew
                         * « Gestionnaire non renseigné » , which reads as « nobody follows this partner »
                         * about partners who are followed. `SignalView` carries `organisationId` and
                         * `partnerName` and no manager fact at all: the signals come from `dossier`'s door,
                         * and giving them the follower is a contract change in another module's answer, so
                         * it is FILED for the round rather than guessed at here. A drawing that states a
                         * fact it does not have is worse than a column that is absent.
                         */}
                        {signal.confirmation === null ? null : (
                          <span
                            data-testid="signal-confirmation"
                            className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"
                          >
                            {signal.confirmation}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm leading-snug text-gray-500">{signal.reason}</p>
                      {signal.actions.length === 0 ? null : (
                        <div className="mt-2.5 flex items-center gap-2">
                          {signal.actions.map((action) => (
                            <button
                              key={action.label}
                              type="button"
                              data-testid={action.kind === "open" ? "signal-ouvrir" : "signal-geste"}
                              disabled={props.busy}
                              onClick={() => {
                                if (action.kind === "open") props.onOpenPartner(signal.organisationId);
                                else props.onAction(signal, action);
                              }}
                              className={
                                "rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-40 " +
                                (action.primary
                                  ? "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700"
                                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")
                              }
                            >
                              {action.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ),
        )}
      </div>

      {/* `K-1`, `M3 (d)`: the reservations , signals 11 and 12 in the cards' own shape. */}
      {props.holdSignals === undefined ? null : (
        <>
          <h2 className="mb-1 text-sm font-semibold">{t("console.priorites.reservationsTitre")}</h2>
          <p className="mb-3 text-sm text-gray-500">{t("console.priorites.reservationsSous")}</p>
          <div
            data-testid="priorites-reservations"
            className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white"
          >
            {props.holdSignals === null ? (
              <p className="px-5 py-5 text-sm text-gray-500">{t("common.state.chargement")}</p>
            ) : props.holdSignals.length === 0 ? (
              <p data-testid="reservations-vide" className="px-5 py-5 text-sm text-gray-500">
                {t("console.priorites.reservationsVide")}
              </p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {props.holdSignals.map((signal) => (
                  <li
                    key={signal.key}
                    data-testid={`reservation-${String(signal.number)}-${signal.dossierId}`}
                    className="flex items-start gap-3 px-5 py-4"
                  >
                    <span
                      className={
                        "mt-1.5 h-2 w-2 shrink-0 rounded-full " +
                        (signal.number === 12 ? "bg-rose-500" : "bg-amber-500")
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{signal.title}</p>
                        <span className="text-xs text-gray-500">
                          {signal.reference} · {signal.clientName}
                        </span>
                      </div>
                      <p data-testid="reservation-raison" className="mt-1 text-sm leading-snug text-gray-500">
                        {signal.reason}
                      </p>
                      <div className="mt-2.5 flex items-center gap-2">
                        {signal.number === 11 ? (
                          <button
                            type="button"
                            data-testid="reservation-relancer"
                            disabled={props.busy}
                            onClick={() => {
                              props.onChaseClient?.(signal.dossierId);
                            }}
                            className="rounded-lg border border-indigo-600 bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                          >
                            {t("console.priorites.reservationRelancer")}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          data-testid="reservation-voir"
                          onClick={() => {
                            props.onOpenDossier?.(signal.dossierId);
                          }}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {t("console.priorites.reservationVoir")}
                        </button>
                        <button
                          type="button"
                          data-testid="reservation-clore"
                          onClick={() => {
                            props.onOpenDossier?.(signal.dossierId);
                          }}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {t("console.priorites.reservationClore")}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {inWords.length === 0 ? null : (
        <>
          <h2 className="mb-1 text-sm font-semibold">{t("console.priorites.enMotsTitre")}</h2>
          <div
            data-testid="priorites-en-mots"
            className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white"
          >
            <ul className="divide-y divide-gray-50">
              {inWords.map((one) => (
                <li
                  key={one.number}
                  data-testid={`signal-mots-${String(one.number)}`}
                  className="flex items-start gap-3 bg-gray-50 px-5 py-4"
                >
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-gray-300" />
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-sm font-medium text-gray-700"
                      title={fill("console.priorites.enMotsPorte", { module: one.owner })}
                    >
                      {one.title}
                    </p>
                    <p className="mt-1 text-sm leading-snug text-gray-500">{one.sentence}</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {fill("console.priorites.enMotsPorte", { module: one.owner })}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      <h2 className="mb-1 text-sm font-semibold">{t("console.priorites.actionsTitre")}</h2>
      <p className="mb-3 text-sm text-gray-500">{t("console.priorites.actionsSous")}</p>
      <div
        data-testid="priorites-actions"
        className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
      >
        {actions.length === 0 ? (
          <p className="px-5 py-5 text-sm text-gray-500">{t("console.priorites.actionsVide")}</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {actions.map((signal) => (
              <li key={signal.key} className="flex items-center gap-3 px-5 py-3">
                <span className="text-sm text-gray-900">{signal.partnerName}</span>
                <span className="text-xs text-gray-500">{signal.confirmation}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export interface PrioritesScreenProps extends PrioritesDoors {
  readonly onOpenDossier?: (dossierId: string) => void;
  readonly vue: Vue;
  readonly onOpenPartner: (organisationId: string) => void;
}

/** The container: the door read at mount and after each gesture. */
export function PrioritesScreen(props: PrioritesScreenProps): React.JSX.Element {
  const [signals, setSignals] = useState<SignalsView | null>(null);
  const [holdSignals, setHoldSignals] = useState<readonly HoldSignalView[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const { readSignals, readHoldSignals } = props;

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const answered = await readSignals();
        if (run.live) setSignals(answered);
        // `K-1`: the reservations, their own part; a refused part leaves the partners' signals standing.
        if (readHoldSignals !== undefined) {
          const holds = await readHoldSignals().catch(() => [] as readonly HoldSignalView[]);
          if (run.live) setHoldSignals(holds);
        }
      } catch (failure) {
        if (run.live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readSignals, readHoldSignals, epoch]);

  return (
    <Priorites
      vue={props.vue}
      signals={signals}
      busy={busy}
      refusal={refusal}
      onOpenPartner={props.onOpenPartner}
      {...(props.readHoldSignals === undefined ? {} : { holdSignals })}
      {...(props.onOpenDossier === undefined ? {} : { onOpenDossier: props.onOpenDossier })}
      onChaseClient={(dossierId) => {
        void (async () => {
          setBusy(true);
          setRefusal(null);
          try {
            await props.chaseClient?.(dossierId);
          } catch (failure) {
            setRefusal(
              (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
            );
          } finally {
            setBusy(false);
          }
        })();
      }}
      onAction={(signal, action) => {
        void (async () => {
          setBusy(true);
          setRefusal(null);
          try {
            await props.logExchange(signal.organisationId, {
              type: action.channel ?? "appel",
              body: action.body ?? action.label,
              origin: action.origin ?? "point",
            });
            setEpoch((now) => now + 1);
          } catch (failure) {
            setRefusal(
              (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
            );
          } finally {
            setBusy(false);
          }
        })();
      }}
    />
  );
}
